import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BrandMark } from './BrandMark';
import { useAuth } from '../context/useAuth';
import { useAdmin } from '../context/useAdmin';
import type { Well } from '../lib/types';

interface Props {
  /** "well" shows the well picker; "wells" shows the My Wells link instead. */
  context?: 'well' | 'wells';
  wells?: Well[];
  activeWellId?: number;
  onSelectWell?: (id: number) => void;
}

export function Header({ context = 'well', wells = [], activeWellId, onSelectWell }: Props) {
  // Defensive on purpose: a wrong shape from the API used to throw here and
  // unmount the entire app, turning a bad response into a white screen.
  const list = Array.isArray(wells) ? wells : [];
  const { user, logout } = useAuth();
  const { access, previewAs } = useAdmin();
  const mayAdminister = access.admin.structure || access.admin.users;

  // A superadmin belongs to no company, so there is no "my wells": the way
  // back is to the companies, and a company leads to its wells.
  const acrossCompanies = access.kind === 'superadmin';
  const home = acrossCompanies ? '/web/Companies' : '/web/MyWells';

  // While previewing, the bar speaks for the person being looked at.
  const shownName = access.subject && access.preview ? access.subject.name : user?.name;
  const shownEmail = access.subject && access.preview ? access.subject.email : user?.email;
  const navigate = useNavigate();
  const [wellOpen, setWellOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const barRef = useRef<HTMLElement>(null);

  const activeWell = list.find((w) => w.id === activeWellId);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!barRef.current?.contains(e.target as Node)) {
        setWellOpen(false);
        setUserOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  return (
    <>
    <header className="appbar" ref={barRef}>
      <Link to={home} className="appbar-brand">
        <BrandMark />
      </Link>

      {/* Off for a superadmin for now: the logo already leads to the companies. */}
      {context === 'wells' && acrossCompanies ? null : context === 'wells' ? (
        <Link to={home} className="well-picker well-picker--link">
          <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
            <path
              d="M2 2h4v4H2zM8 2h4v4H8zM14 2h4v4h-4zM2 8h4v4H2zM8 8h4v4H8zM14 8h4v4h-4zM2 14h4v4H2zM8 14h4v4H8zM14 14h4v4h-4z"
              fill="currentColor"
            />
          </svg>
          My Wells
          <Caret />
        </Link>
      ) : (
        <div className="menu">
          <button
            type="button"
            className="well-picker"
            aria-expanded={wellOpen}
            onClick={() => {
              setWellOpen((v) => !v);
              setUserOpen(false);
            }}
          >
            {activeWell?.name ?? 'Select well'}
            <Caret />
          </button>

          {wellOpen && (
            <ul className="dropdown" role="listbox">
              {list.map((w) => (
                <li key={w.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={w.id === activeWellId}
                    className={w.id === activeWellId ? 'is-active' : ''}
                    onClick={() => {
                      onSelectWell?.(w.id);
                      setWellOpen(false);
                    }}
                  >
                    <span>{w.name}</span>
                    <small>
                      {w.field} · {w.rig}
                    </small>
                  </button>
                </li>
              ))}
              <li className="dropdown-sep">
                <button type="button" onClick={() => navigate(home)}>
                  <span>{acrossCompanies ? 'All companies…' : 'All wells…'}</span>
                </button>
              </li>
            </ul>
          )}
        </div>
      )}

      <div className="appbar-spacer" />

      <button type="button" className="icon-btn" title="Help" aria-label="Help">
        {/* Solid disc, the question mark cut out of it in white. */}
        <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
          <circle cx="10" cy="10" r="9" fill="currentColor" />
          <path
            d="M7.6 7.6a2.4 2.4 0 1 1 3.3 2.2c-.65.27-.95.75-.95 1.45v.45"
            fill="none"
            stroke="#fff"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <circle cx="10" cy="14.6" r="1.05" fill="#fff" />
        </svg>
      </button>

      {/* On a phone there is no room for it; the same page is in the person's menu. */}
      <button
        type="button"
        className="icon-btn appbar-settings"
        title="Settings"
        aria-label="Settings"
        onClick={() => navigate('/web/UserProfile/EditProfile')}
      >
        <GearIcon size={18} />
        <Caret />
      </button>

      <div className="menu">
        <button
          type="button"
          className="user-btn"
          aria-expanded={userOpen}
          onClick={() => {
            setUserOpen((v) => !v);
            setWellOpen(false);
          }}
        >
          <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true">
            <circle cx="10" cy="6.4" r="3.4" fill="currentColor" />
            <path d="M3.4 17.2a6.6 6.6 0 0 1 13.2 0Z" fill="currentColor" />
          </svg>
          <span className="user-btn-name">{shownName}</span>
          <Caret />
        </button>

        {userOpen && (
          <ul className="dropdown dropdown--right">
            <li className="dropdown-meta">
              <div className="dropdown-meta-text">
                <strong>{shownName}</strong>
                <small>{shownEmail}</small>
                <small>{access.kind === 'legacy' ? user?.role : access.roleName}</small>
              </div>
              {/* The bar's gear, beside the person it belongs to — on a phone
                  this is the only one, as the bar has no room for it. */}
              <button
                type="button"
                className="dropdown-meta-gear"
                title="Settings"
                aria-label="Settings"
                onClick={() => {
                  setUserOpen(false);
                  navigate('/web/UserProfile/EditProfile');
                }}
              >
                <GearIcon size={20} />
              </button>
            </li>
            <li>
              <button
                type="button"
                onClick={() => {
                  setUserOpen(false);
                  navigate('/web/UserProfile/EditProfile');
                }}
              >
                <span>Edit Profile Settings</span>
              </button>
            </li>
            {access.kind === 'superadmin' && (
              <li>
                <button
                  type="button"
                  onClick={() => {
                    setUserOpen(false);
                    navigate('/web/KnowledgeBase');
                  }}
                >
                  <span>Knowledge Base</span>
                </button>
              </li>
            )}
            {mayAdminister && (
              <li>
                <button
                  type="button"
                  onClick={() => {
                    setUserOpen(false);
                    navigate('/web/Admin');
                  }}
                >
                  <span>Administration</span>
                </button>
              </li>
            )}
            <li>
              <button type="button" onClick={logout}>
                <span>Sign out</span>
              </button>
            </li>
          </ul>
        )}
      </div>
    </header>

    {access.preview && access.subject && (
      <div className="previewbar" role="status">
        <span>
          Viewing as <strong>{access.subject.name}</strong> ({access.roleName}). This is a preview of
          what they see; you are still signed in as {user?.name}.
        </span>
        <button
          type="button"
          className="kbbtn"
          onClick={() => {
            previewAs(null);
            navigate('/web/Admin/Users');
          }}
        >
          Stop viewing
        </button>
      </div>
    )}
    </>
  );
}

/**
 * A solid toothed gear with a white hub, to match the solid help disc. The old
 * one, a ring with eight rays, read as a sun.
 */
function GearIcon({ size }: { size: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <path
        fill="currentColor"
        d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
      />
      <circle cx="12" cy="12" r="3.2" fill="#fff" />
    </svg>
  );
}

function Caret() {
  return (
    <svg className="caret" viewBox="0 0 10 6" width="9" height="6" aria-hidden="true">
      <path d="M0 0h10L5 6Z" fill="currentColor" />
    </svg>
  );
}
